import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Modal} from "../../../../../../components/modal/modal.jsx";
import Button from "../../../../../../components/button/button.jsx";
import InputCheckbox from "../../../../../../components/input/input-checkbox.jsx";
import {FaCheck, FaClock} from "react-icons/fa";
import {FaTriangleExclamation} from "react-icons/fa6";
import {hasWindowMismatch, matchingAddresses} from "../../utils/schedule-utils.js";

const AddressOption = ({address, selected, mismatch, onToggle}) => {
    const main = [address.address, address.number].filter(Boolean).join(", ") || "Endereço";
    const sub = [address.neighborhood, address.city].filter(Boolean).join(" — ");

    const tags = [];
    if (address.visitable) tags.push("Visita");
    if (address.patrollable) tags.push("Ronda");

    return (
        <button
            type="button"
            onClick={() => onToggle(address.id)}
            className={[
                "flex items-start gap-3 text-left w-full rounded-lg border-2 p-3.5 cursor-pointer transition-colors",
                selected ? "border-[#063A6C] bg-blue-50" : "border-gray-200 hover:border-[#063A6C]/50"
            ].join(" ")}
        >
            <span className={[
                "w-[18px] h-[18px] mt-0.5 rounded-[5px] border-[1.5px] grid place-items-center shrink-0 transition-colors",
                selected ? "bg-[#063A6C] border-[#063A6C] text-white" : "bg-white border-gray-300"
            ].join(" ")}>
                {selected && <FaCheck size={11}/>}
            </span>

            <div className={"flex-1 min-w-0"}>
                <div className={"text-[13.5px] font-semibold text-gray-800"}>{main}</div>
                {sub && <div className={"text-xs text-gray-500 mt-0.5"}>{sub}</div>}

                <div className={"flex items-center gap-1.5 flex-wrap mt-1.5"}>
                    {
                        tags.map(tag => (
                            <span key={tag} className={"text-[10.5px] font-bold uppercase tracking-wide text-gray-500 bg-gray-100 border border-gray-200 rounded px-1.5 py-0.5"}>
                                {tag}
                            </span>
                        ))
                    }
                    <span className={["inline-flex items-center gap-1 text-[11px] font-semibold", mismatch ? "text-[#e19300]" : "text-gray-500"].join(" ")}>
                        <FaClock size={11}/>
                        {address.availabilityStartTime || "—"} – {address.availabilityEndTime || "—"}
                    </span>
                </div>
            </div>
        </button>
    );
};

const ScheduleConflictModal = ({open, setOpen, citizen, schedule, selectedIds, confirmed, onConfirm}) => {
    const [localIds, setLocalIds] = useState([]);
    const [localConfirmed, setLocalConfirmed] = useState(false);

    useEffect(() => {
        if (!open) return;

        setLocalIds(selectedIds ?? []);
        setLocalConfirmed(!!confirmed);
    }, [open]);

    const lastCitizenRef = useRef(citizen);
    if (citizen) lastCitizenRef.current = citizen;
    const shownCitizen = citizen ?? lastCitizenRef.current;

    const matches = useMemo(() => shownCitizen ? matchingAddresses(shownCitizen, schedule.type) : [], [shownCitizen, schedule.type]);

    const multi = matches.length > 1;
    const selectedAddresses = matches.filter(address => localIds.includes(address.id));
    const mismatchAddresses = selectedAddresses.filter(address => hasWindowMismatch(address, schedule));
    const hasMismatch = mismatchAddresses.length > 0;
    const canConfirm = localIds.length > 0 && (!hasMismatch || localConfirmed);

    const toggle = (id) => setLocalIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

    const confirm = () => {
        if (!shownCitizen) return;

        onConfirm(shownCitizen.id, localIds, hasMismatch ? localConfirmed : false);
        setOpen(false);
    };

    const title = (
        <div className={"flex items-center gap-3"}>
            <div className={"w-9 h-9 rounded-lg grid place-items-center bg-orange-100 text-[#fbae1d] shrink-0"}>
                <FaTriangleExclamation size={18}/>
            </div>
            <div className={"leading-tight"}>
                <div className={"text-[15px] font-bold text-gray-800"}>Resolver conflito</div>
                {shownCitizen && <div className={"text-xs font-normal text-gray-500"}>{shownCitizen.name}</div>}
            </div>
        </div>
    );

    return (
        <Modal open={open} setOpen={setOpen} title={title} className={"w-2xl"}>
            <Modal.Body className={"space-y-4"}>
                {
                    multi &&
                    <section className={"space-y-2"}>
                        <div>
                            <h4 className={"text-sm font-bold text-gray-700"}>Endereços do protegido</h4>
                            <p className={"text-xs text-gray-500 mt-0.5"}>
                                Este protegido tem mais de um endereço. Selecione um ou mais para concluir o agendamento.
                            </p>
                        </div>

                        <div className={"flex flex-col gap-2.5 max-h-[46vh] overflow-auto pr-1"}>
                            {
                                matches.map(address => (
                                    <AddressOption
                                        key={address.id}
                                        address={address}
                                        selected={localIds.includes(address.id)}
                                        mismatch={hasWindowMismatch(address, schedule)}
                                        onToggle={toggle}
                                    />
                                ))
                            }
                        </div>
                    </section>
                }

                {
                    hasMismatch &&
                    <div className={"bg-orange-50 border border-orange-200 rounded-lg p-3.5"}>
                        <div className={"flex items-center gap-2 text-[13px] font-bold text-[#8a6212] mb-1.5"}>
                            <FaClock size={15}/>
                            Horário diferente do cadastrado
                        </div>
                        <p className={"text-[12.5px] text-[#8a6212] mb-3"}>
                            O agendamento é para <b>{schedule.startTime} – {schedule.endTime}</b>, mas o endereço da Visita/Ronda está configurado
                            para <b>{mismatchAddresses.map(a => `${a.availabilityStartTime}–${a.availabilityEndTime}`).join(", ")}</b>. Deseja confirmar mesmo assim?
                        </p>
                        <InputCheckbox
                            id={"confirm-time-mismatch"}
                            label={"Confirmar agendamento fora do horário cadastrado"}
                            checked={localConfirmed}
                            onChange={({target: {checked}}) => setLocalConfirmed(checked)}
                        />
                    </div>
                }

                {
                    shownCitizen && !multi && !hasMismatch &&
                    <div className={"flex items-center gap-2 text-[13px] font-semibold text-green-600 py-2"}>
                        <FaCheck size={16}/>
                        Sem conflitos neste endereço.
                    </div>
                }
            </Modal.Body>

            <Modal.Footer>
                <Button onClick={confirm} disabled={!canConfirm}>
                    Confirmar
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ScheduleConflictModal;
